import React, { useState, useMemo, useEffect } from 'react';

// --- Icons using Lucide React and Custom SVG ---
// Fallback/Placeholder icons (simulated import)
const FolderIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 6.4 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/><path d="M12 10v6"/><path d="M9 13h6"/></svg>;
const ZapIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.8 15.6 15.6 10.8 21 3 13.2 8.4 8.4 13.2 3 21 10.8 15.6z"/></svg>;
const CheckIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>;
const SearchIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>;
const GlobeIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20zM2 12h20"/></svg>;
const CodeIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 16 4-4-4-4"/><path d="m6 8-4 4 4 4"/><path d="m14.5 4-5 16"/></svg>;
const XIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;

// --- Constants & Data ---

const PRIMARY_COLOR = 'green-600';
const PRIMARY_HOVER = 'green-700';
const PRIMARY_BG_LIGHT = 'green-50';

const TEMPLATES = [
    {
        id: 'se_s',
        title: 'Senior Software Engineer (Backend)',
        description: 'A detailed template covering 6+ years experience, focusing on NodeJS, cloud infrastructure (AWS/GCP), and microservices architecture.',
        tags: ['IT', 'Senior', 'NodeJS', 'Microservices'],
        complexity: 'High',
        fullDescription: `# Senior Software Engineer (Backend)

## About the Role
You will be a core member of our engineering team, responsible for designing, building, and maintaining high-performance, scalable backend services. You will mentor junior engineers and drive technical decisions.

## Key Responsibilities
* Lead the design and implementation of new microservices using NodeJS.
* Optimize database queries and system architecture for maximum speed and scale.
* Manage deployment and monitoring in AWS/GCP environment.
* Conduct code reviews and champion best coding practices (e.g., TDD, clean architecture).

## Qualifications
* 6+ years of professional experience in backend development.
* Expertise in a high-level programming language (NodeJS, Python, or Go).
* Deep knowledge of SQL/NoSQL databases (PostgreSQL, MongoDB).
* Proven experience with cloud services (AWS/GCP).
`
    },
    {
        id: 'pm_mid',
        title: 'Mid-Level Product Manager',
        description: 'Standard JD for a PM with 3-5 years experience, emphasizing agile methodologies, roadmap ownership, and cross-functional collaboration.',
        tags: ['Product', 'Mid', 'Agile', 'Roadmap'],
        complexity: 'Medium',
        fullDescription: `# Mid-Level Product Manager

## The Product Visionary
We're looking for an enthusiastic Product Manager to own and execute the product roadmap for a key feature set. You will bridge the gap between business goals, technology, and user needs.

## Key Responsibilities
* Define and prioritize user stories and epics in alignment with the overall product strategy.
* Lead daily scrum and planning meetings within an Agile framework.
* Analyze market trends, competition, and user data to identify opportunities.
* Collaborate closely with Engineering, Design, and Marketing teams.

## Qualifications
* 3-5 years of product management experience.
* Strong understanding of Agile/Scrum methodologies.
* Excellent written and verbal communication skills.
* Experience with prototyping and wireframing tools.
`
    },
    {
        id: 'mkt_jr',
        title: 'Junior Marketing Associate (Digital)',
        description: 'Focuses on entry-level skills like social media management, content creation, and basic SEO/SEM campaign execution.',
        tags: ['Marketing', 'Entry', 'SEO', 'Social Media'],
        complexity: 'Low',
        fullDescription: `# Junior Marketing Associate (Digital)

## The Team Player
This entry-level role is perfect for a highly motivated individual eager to learn the ropes of digital marketing. You'll support our team in executing campaigns across various platforms.

## Key Responsibilities
* Assist in managing daily posts and engagement across all social media channels.
* Draft, edit, and proofread content for blogs, email newsletters, and website pages.
* Monitor and report on campaign performance using basic analytics tools.
* Conduct basic keyword research for SEO optimization.

## Qualifications
* Bachelor's degree in Marketing, Communications, or related field.
* Familiarity with major social media platforms (LinkedIn, Twitter, etc.).
* Basic knowledge of SEO/SEM concepts is a plus.
* Strong organizational skills and attention to detail.
`
    },
    {
        id: 'hr_lead',
        title: 'HR Lead / People Operations Head',
        description: 'A comprehensive template for leadership roles, including talent acquisition strategy, employee engagement, and policy development.',
        tags: ['HR', 'Leadership', 'Strategy', 'Policy'],
        complexity: 'High',
        fullDescription: `# HR Lead / People Operations Head

## The People Champion
You will lead the People Operations function, shaping the employee experience from onboarding to exit. This role requires strategic vision and hands-on operational excellence.

## Key Responsibilities
* Develop and implement comprehensive talent acquisition and retention strategies.
* Oversee performance management, compensation planning, and employee relations.
* Ensure legal compliance and maintain up-to-date HR policies and procedures.
* Drive employee engagement and culture-building initiatives.

## Qualifications
* 8+ years of progressive HR/People Operations experience, with 3+ years in a leadership role.
* Extensive knowledge of labor laws and compliance requirements.
* Exceptional conflict resolution and communication skills.
* Experience scaling an HR function in a fast-paced environment.
`
    },
    {
        id: 'ux_d',
        title: 'UI/UX Designer',
        description: 'Design job description emphasizing Figma proficiency, user research, wireframing, and creating high-fidelity prototypes.',
        tags: ['Design', 'Mid', 'Figma', 'UX Research'],
        complexity: 'Medium',
        fullDescription: `# UI/UX Designer

## The Experience Architect
You will be responsible for translating user needs and business requirements into intuitive and beautiful interfaces across our product suite.

## Key Responsibilities
* Create detailed wireframes, prototypes, and high-fidelity mockups using Figma.
* Plan and conduct user research (interviews, usability testing) to inform design decisions.
* Maintain and evolve our design system to ensure consistency and scalability.
* Work iteratively with product managers and engineers throughout the development cycle.

## Qualifications
* 3+ years of professional UI/UX design experience.
* Expert proficiency in Figma or similar design tools.
* A strong portfolio demonstrating problem-solving skills and visual design excellence.
* Experience defining information architecture and user flows.
`
    },
];

/**
 * Global Modal Component for Preview
 */
const Modal = ({ title, content, isOpen, onClose }) => {
    // Disable scrolling on the body when the modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset'; // Clean up on unmount
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-75 transition-opacity duration-300">
            {/* Modal Content: 
                - max-h-[70vh] ensures it's smaller than the viewport height and stays centered.
            */}
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[70vh] overflow-hidden relative transform scale-100 transition-transform duration-300 border border-gray-200">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                    <h2 className="text-xl font-poppins font-bold text-gray-800">
                        Preview: {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition"
                        aria-label="Close"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
                {/* Scrollable content area:
                    - max-h-[calc(70vh-70px)] adjusts the scroll height based on the new max-h-[70vh] 
                      to account for the header height (approx 70px).
                */}
                <div className="p-6 overflow-y-auto max-h-[calc(70vh-70px)]">
                    {/* Render Markdown content */}
                    <div className="prose max-w-none font-roboto text-gray-700">
                        <div dangerouslySetInnerHTML={{ __html: content }} />
                    </div>
                </div>
            </div>
        </div>
    );
};


/**
 * Reusable Template Card Component
 */
const TemplateCard = ({ template, isSelected, onSelect }) => {
    const Icon = template.title.includes('Engineer') ? CodeIcon : template.title.includes('Product') ? ZapIcon : GlobeIcon;

    return (
        <div
            className={`
                bg-white p-4 rounded-lg border transition-all duration-300 cursor-pointer h-full flex flex-col justify-between
                ${isSelected ? `border-2 border-${PRIMARY_COLOR} shadow-lg ring-2 ring-${PRIMARY_BG_LIGHT}` : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}
            `}
            onClick={() => onSelect(template.id)}
        >
            <div>
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                        <Icon className={`w-5 h-5 ${isSelected ? `text-${PRIMARY_COLOR}` : 'text-gray-500'}`} />
                        <h3 className="text-base font-poppins font-semibold text-gray-800 leading-snug">
                            {template.title}
                        </h3>
                    </div>
                    {isSelected && (
                        <CheckIcon className={`w-4 h-4 text-${PRIMARY_COLOR} font-bold mt-0.5`} />
                    )}
                </div>

                <p className="text-xs text-gray-500 mb-3 font-roboto min-h-[50px] leading-relaxed">
                    {template.description}
                </p>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100">
                {template.tags.map(tag => (
                    <span
                        key={tag}
                        className={`text-xs px-2 py-0.5 rounded font-roboto font-medium transition-colors
                            ${isSelected ? `bg-${PRIMARY_COLOR} text-white` : 'bg-gray-100 text-gray-600'}
                        `}
                    >
                        {tag}
                    </span>
                ))}
            </div>
        </div>
    );
};

// Simple Markdown to HTML converter (for the modal content)
const mdToHtml = (markdown) => {
    // Basic Markdown conversion for simplicity in a single React file
    let html = markdown
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/\*\*([^*]+)\*\*/gim, '<strong>$1</strong>')
        .replace(/\* (.*$)/gim, '<li>$1</li>')
        .replace(/^(<li>.*<\/li>)+$/gims, '<ul>$&</ul>'); // Improved list capture

    // Clean up multiple <br/> from list replacements
    html = html.replace(/<br\/>(<ul>)/g, '$1');
    html = html.replace(/<\/li><br\/>/g, '</li>');

    // Remove any leading/trailing <br/> left over
    html = html.replace(/^(<br\/>)+/g, '');
    html = html.replace(/(<br\/>)+$/g, '');

    // Replace remaining newlines with <br/> for general paragraphs
    html = html.replace(/\n/g, '<br/>');


    return html;
};


/**
 * Main JD Templates Component
 */
const JdTemplates = () => {
    const [selectedTemplateId, setSelectedTemplateId] = useState(TEMPLATES[0].id);
    const [searchTerm, setSearchTerm] = useState('');
    const [isApplied, setIsApplied] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    const handleSelect = (id) => {
        setSelectedTemplateId(id);
        setIsApplied(false); // Reset applied state when a new template is selected
    };

    const handleApply = () => {
        // Simulate applying the template data to a job posting form
        console.log(`Applying template ID: ${selectedTemplateId}`);
        setIsApplied(true);
        // In a real app, this would route the user back to the job creation page
    };

    const handlePreview = () => {
        setIsPreviewModalOpen(true);
    };

    const filteredTemplates = useMemo(() => {
        if (!searchTerm) return TEMPLATES;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return TEMPLATES.filter(template =>
            template.title.toLowerCase().includes(lowerCaseSearch) ||
            template.description.toLowerCase().includes(lowerCaseSearch) ||
            template.tags.some(tag => tag.toLowerCase().includes(lowerCaseSearch))
        );
    }, [searchTerm]);

    // Get the currently selected template object
    const selectedTemplate = TEMPLATES.find(t => t.id === selectedTemplateId);
    const previewContentHtml = selectedTemplate ? mdToHtml(selectedTemplate.fullDescription) : '';

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-6 sm:p-8 font-roboto">
            {/* Global Font Styles */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap');
                    .font-poppins {
                        font-family: 'Poppins', sans-serif;
                    }
                    .font-roboto {
                        font-family: 'Roboto', sans-serif;
                    }
                    /* Custom utility for the vibrant green */
                    .bg-green-600 { background-color: #059669; }
                    .hover\\:bg-green-700:hover { background-color: #047857; }
                    .text-green-600 { color: #059669; }
                    .border-green-600 { border-color: #059669; }
                    .ring-green-50 { --tw-ring-color: #f0fdf4; }
                    /* Style the content of the modal using 'prose' style */
                    .prose h1 { font-size: 1.8rem; font-weight: 700; margin-bottom: 1rem; color: #1f2937; font-family: 'Poppins', sans-serif; }
                    .prose h2 { font-size: 1.4rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.75rem; color: #374151; border-bottom: 1px solid #f3f4f6; padding-bottom: 4px; font-family: 'Poppins', sans-serif; }
                    .prose p { margin-bottom: 1rem; line-height: 1.6; }
                    .prose ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
                    .prose li { margin-bottom: 0.5rem; }
                    /* Ensure list items and paragraphs have proper line breaks */
                    .prose li + li { margin-top: 0.5em; }
                    .prose br { display: none; } /* Hide default br tags, prose handles newlines */
                    .prose p + p { margin-top: 1em; }
                    .prose ul + p { margin-top: 1em; }
                    .prose h2 + ul { margin-top: 0.5em; }
                    .prose ul:last-child { margin-bottom: 0; }
                `}
            </style>

            <div className="max-w-6xl w-full bg-white rounded-xl shadow-2xl border border-gray-100 mt-4 overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 bg-white">
                    <h1 className="text-2xl font-poppins font-bold text-gray-800 flex items-center">
                        <FolderIcon className="w-5 h-5 mr-2 text-purple-600" />
                        JD Template Library
                    </h1>
                    <p className="text-xs text-gray-500 mt-1 font-roboto">
                        Select a template to quickly create your job posting.
                    </p>
                </div>

                {/* Main Content Area */}
                <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Left Column: Template List (2/3 width) */}
                    <div className="md:col-span-2 space-y-4">
                        {/* Search Bar */}
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search by title, skill, or department..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full p-2 pl-9 border border-gray-300 rounded-lg focus:ring-1 focus:ring-purple-300 focus:border-purple-500 transition duration-150 shadow-sm text-sm font-roboto"
                            />
                            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredTemplates.length > 0 ? (
                                filteredTemplates.map(template => (
                                    <TemplateCard
                                        key={template.id}
                                        template={template}
                                        isSelected={template.id === selectedTemplateId}
                                        onSelect={handleSelect}
                                    />
                                ))
                            ) : (
                                <div className="sm:col-span-2 lg:col-span-3 text-center p-8 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-yellow-700 font-semibold text-sm">No templates found matching "{searchTerm}".</p>
                                    <p className="text-xs text-yellow-600 mt-1">Refine your search.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Preview & Action (1/3 width) */}
                    <div className="md:col-span-1">
                        <div className="sticky top-6 bg-white p-4 rounded-xl border border-gray-200 shadow-lg space-y-4">
                            <h2 className="text-lg font-poppins font-bold text-gray-800">
                                Selected Template
                            </h2>
                            {selectedTemplate ? (
                                <>
                                    <div className={`p-3 rounded-lg border-l-4 ${isApplied ? 'border-green-500 bg-green-50' : 'border-purple-500 bg-purple-50'}`}>
                                        <p className="text-sm font-medium text-gray-700">
                                            {selectedTemplate.title}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {selectedTemplate.tags.join(' / ')}
                                        </p>
                                    </div>

                                    {/* Action Buttons - Reduced Padding/Size */}
                                    <button
                                        onClick={handleApply}
                                        disabled={isApplied}
                                        className={`w-full py-2 text-sm rounded-lg font-poppins font-bold transition duration-150 shadow-md text-white
                                            ${isApplied
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : `bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER}`
                                            }
                                        `}
                                    >
                                        {isApplied ? (
                                            <span className="flex items-center justify-center">
                                                <CheckIcon className="w-4 h-4 mr-2" />
                                                Applied!
                                            </span>
                                        ) : 'Use This Template & Proceed'}
                                    </button>
                                    <button
                                        onClick={handlePreview}
                                        className="w-full py-2 text-sm rounded-lg font-poppins font-semibold border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-150 shadow-sm"
                                    >
                                        Preview Full JD
                                    </button>
                                </>
                            ) : (
                                <div className="p-3 text-center text-sm text-gray-500 bg-gray-50 rounded-lg">
                                    No template selected.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            <Modal
                title={selectedTemplate?.title || 'Job Description Preview'}
                content={previewContentHtml}
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
            />
        </div>
    );
};

export default JdTemplates;